import "../style.css";
import Button from "components/Buttons";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useMemo } from "react";
import WildHorse from "assets/images/saddle_logo_icon.png";
import { EditIcon, TrashIcon } from "components/Icons";
import { formatDate, formatToMMDDYYYY } from "utils";
import { useHorseStore } from "stores/horse/useHorseStore";
import { useModalStore, Modal as ModalKey } from "stores/modal/useModalStore";
import { useQuery } from "@apollo/client";
import { LIST_MY_CUSTOM_SADDLE_ORDERS } from "apollo/mutations/customSaddleOrderRequest";

const HorseProfileCard = ({
  data,
  onHandleEditClick,
  onHandleDelete,
  selectAllHorsesIds,
}) => {
  const navigate = useNavigate();
  const selectAll = useHorseStore((state) => state.selectAll);
  const isDeletedSuccessfully = useHorseStore(
    (state) => state.isDeletedSuccessfully
  );
  const setHorseData = useHorseStore((action) => action.setHorseData);
  const setDeleteHorses = useHorseStore((action) => action.setDeleteHorses);
  const open = useModalStore((s) => s.open);
  const [, setIsChecked] = useState(false);
  const timestamp = data?.createdAt;
  const formattedDate = formatDate(timestamp);

  const isScanned = data?.status === "SCAN" || data?.status === "LIVE";

  // Fetch existing custom saddle orders to check for pending status
  const { data: ordersData } = useQuery(LIST_MY_CUSTOM_SADDLE_ORDERS, {
    fetchPolicy: "cache-and-network",
  });

  const latestOrder = useMemo(() => {
    if (!ordersData?.listMyCustomSaddleOrders?.items || !data?.name) return null;
    return ordersData.listMyCustomSaddleOrders.items
      .filter((order) => order.horseName === data.name)
      .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))[0];
  }, [ordersData, data]);

  const getStatusConfig = (status) => {
    switch (status) {
      case "REQUESTED":
        return { label: "Requested", className: "status-pending", blocked: true };
      case "APPROVED":
        return { label: "Order Approved", className: "status-approved", blocked: true };
      case "SUBMITTED_TO_CACTUS":
        return { label: "In Production", className: "status-production", blocked: true };
      case "PROCESSING":
        return { label: "Processing", className: "status-processing", blocked: true };
      case "REJECTED":
        return { label: "Request Rejected", className: "status-rejected", blocked: true };
      case "SHIPPED":
        return { label: "Order Completed", className: "status-completed", blocked: true };
      default:
        return { label: "Request Custom Saddle", className: "", blocked: false };
    }
  };

  const statusConfig = useMemo(() => getStatusConfig(latestOrder?.status), [latestOrder]);

  const isButtonDisabled = !isScanned || statusConfig.blocked;

  const handleRequestCustomSaddle = () => {
    if (isButtonDisabled) return;
    setHorseData(data);
    open(ModalKey.RequestCustomSaddle);
  };

  useEffect(() => {
    setIsChecked(selectAll);
    if (selectAll) {
      selectAllHorsesIds();
    } else {
      selectAllHorsesIds(true);
      setIsChecked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll]);

  useEffect(() => {
    setIsChecked(false);
  }, [isDeletedSuccessfully]);

  return (
    <>
      <div className="clay-card-dashboard !h-[332px] !py-[25px] !px-[25px] !justify-start gap-4 bg-white">

        {/* Header: Name and Chevron */}
        <div className="flex justify-between items-start w-full">
          <div className="flex flex-col flex-1 min-w-0 pr-4">
            <h1
              className="prof-clay-card-text !mt-0 !h-auto !w-full !break-words !line-clamp-2 !leading-[1]"
              title={data?.name && data?.name}
            >
              {data?.name && data?.name}
            </h1>
          </div>
          <img
            src={data?.photo?.trim() ? data.photo : WildHorse}
            alt="horseProfile"
            className={
              data?.photo?.trim()
                ? "dashboard-horse_prof_img !mt-0"
                : "!object-cover !mt-0"
            }
          />
        </div>

        {/* Info Row: DOB, Last Scan, and Action Icons */}
        <div className={`flex items-center justify-between w-full ${data?.photo?.trim() ? "mt-2" : "mt-[53px]"}`}>
          <div className="flex gap-8">
            <div className="flex flex-col">
              <h4 className="!text-[16px] font-bold text-[#2b364b] font-montserrat leading-none mb-1">
                {data?.dob ? formatToMMDDYYYY(data?.dob) : "-"}
              </h4>
              <p className="!text-[12px] text-[#858585] font-montserrat font-medium">
                Date of Birth
              </p>
            </div>
            <div className="flex flex-col">
              <h4 className="!text-[16px] font-bold text-[#2b364b] font-montserrat leading-none mb-1">
                {formattedDate || "-"}
              </h4>
              <p className="!text-[12px] text-[#858585] font-montserrat font-medium">
                Last Scan
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              transparent
              className="!p-0 !min-w-[30px] !w-[30px] !h-[30px] bg-[#FFFFFF] border border-[#EBEBEB] rounded-[8px] flex justify-center items-center "
              onClick={() => {
                setHorseData(data);
                onHandleEditClick();
              }}
              AppendIcon={<EditIcon className="w-[18px] h-[18px] text-[#5C80B6]" />}
            />
            <Button
              transparent
              onClick={() => {
                if (!data?.id) return;
                setDeleteHorses((prev) =>
                  Array.from(new Set([...prev, data.id]))
                );
                onHandleDelete();
              }}
              className="!p-0 !min-w-[30px] !w-[30px]  !h-[30px] bg-[#FFFFFF] border border-[#EBEBEB] rounded-[8px] flex justify-center items-center  group"
              AppendIcon={
                <TrashIcon className="w-[18px] h-[18px] group-hover:text-red-500 transition-colors" stroke="#FF383C" />
              }
            />
          </div>
        </div>

        {/* Dashed Separator */}
        <div className="w-full border-t border-dashed border-[#D6C4B8] my-2"></div>

        {/* Footer Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 justify-between w-full">
          <Button
            transparent
            onClick={handleRequestCustomSaddle}
            disabled={isButtonDisabled}
            className={`w-full sm:flex-1 !tracking-[-0.03em] !border-[2px] !border-[#2B364B] !text-[#2B364B] !text-[12px] sm:!text-[12px] md:!text-[14px] !font-medium !rounded-[30px] !h-[42px] uppercase tracking-wide font-montserrat !min-w-0 whitespace-nowrap !px-4 ${isButtonDisabled
              ? "!opacity-50 !cursor-not-allowed hover:bg-transparent"
              : "hover:bg-gray-50"
              }`}
            title={
              !isScanned
                ? "Horse must be scanned to request custom saddle"
                : statusConfig.blocked
                  ? `An order is already in progress: ${statusConfig.label}`
                  : ""
            }
          >
            {statusConfig.blocked ? statusConfig.label : "Request Custom Saddle"}
          </Button>
          <Button
            primary
            className="w-full sm:!w-auto !tracking-[-0.03em] !bg-[#2B364B] !text-white !text-[12px] sm:!text-[12px] md:!text-[14px] !font-medium !rounded-[30px] !h-[42px] uppercase tracking-wide font-montserrat hover:opacity-90 !min-w-0 whitespace-nowrap !px-4"
            onClick={() => navigate("/event")}
          >
            Schedule Scan
          </Button>
        </div>
      </div>
    </>
  );
};

export default HorseProfileCard;
